import React, { useEffect, useState } from "react";
import { useUpdateEffect } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import QuantityInput from "../QuantityInput/QuantityInput";
import {
  selectTrack,
  selectNbStraightSegments,
  selectNbAngledSegments,
  selectCalculatedGuideRails,
  setCalculatedGuideRails,
  selectShouldRecalculateGuideRails,
  setShouldRecalculateGuideRails,
  selectTrackLength,
  selectSegments,
  selectAccessories,
  setTrack,
  setShouldRecalculateElectricalInstallation,
  setShouldRecalculateCommunication
} from "../../store/slices/mc12";
import { calculateGuideRails, getAccessoriesByDescriptionKeyword } from "../../utils/func.utils";
import { SEGMENT_INF } from "../../utils/constants.utils";
import { getProductImgUrl } from "../../../Common/utils/api/api";
import t from "../../../Common/utils/translation/translator";

const TrackInfo = () => {
  const dispatch = useDispatch();

  const track = useSelector(selectTrack);
  const trackLength = useSelector(selectTrackLength);
  const nbStraightSegments = useSelector(selectNbStraightSegments);
  const nbAngledSegments = useSelector(selectNbAngledSegments);
  const accessories = useSelector(selectAccessories);
  const calculatedGuideRails = useSelector(selectCalculatedGuideRails);
  const shouldRecalculateGuideRails = useSelector(selectShouldRecalculateGuideRails);
  const mc12Segments = useSelector(selectSegments);

  const [displayOpenTrackErrorMessage, setDisplayOpenTrackErrorMessage] = useState(false);

  const getMaxCarriers = () => {
    const maxCarriers =
      mc12Segments.straight.minNbCarrierPerController * nbStraightSegments +
      mc12Segments.angled.minNbCarrierPerController * nbAngledSegments;
    if (maxCarriers > mc12Segments.straight.maxNbCarrierPerController) {
      return mc12Segments.straight.maxNbCarrierPerController;
    }
    return maxCarriers;
  };

  const getNumberOfCarriers = () => {
    // console.log("getNumberOfCarriers");
    const val = track.calculatedCarriers.reduce((acc, carrier) => {
      if (carrier.quantityPerSet === "10_PIECES") {
        return acc + carrier.quantity * 10;
      } else {
        return acc + carrier.quantity;
      }
    }, 0);
    return val;
  };

  const onCalculateGuideRails = (receivedTrack) => {
    // console.log("onCalculateGuideRails");

    // MSC-1576: Prevent to calculate guide rails if the track is open and start or end with angled segment
    if (
      !receivedTrack.isTrackClosed &&
      receivedTrack.length > 0 &&
      receivedTrack.trackPattern &&
      (receivedTrack.trackPattern.at(0).type === SEGMENT_INF.ANGLED.LOGICAL_TYPE ||
        receivedTrack.trackPattern.at(-1).type === SEGMENT_INF.ANGLED.LOGICAL_TYPE)
    ) {
      dispatch(setCalculatedGuideRails([]));
      setDisplayOpenTrackErrorMessage(true);
      return;
    }
    const calculatedGuideRailsNow = calculateGuideRails(
      receivedTrack,
      accessories.filter((gr) => gr.accessoryType === "GUIDE_RAIL"),
      accessories.find((gr) => gr.accessoryType === "HARD_STOPS")
    );
    if (calculatedGuideRailsNow && calculatedGuideRailsNow.length > 0) {
      dispatch(setCalculatedGuideRails(calculatedGuideRailsNow));
    }
    dispatch(setShouldRecalculateGuideRails(false));
    dispatch(setShouldRecalculateElectricalInstallation(true));
    dispatch(setShouldRecalculateCommunication(true));
  };

  useEffect(() => {
    const tmpTrack = JSON.parse(JSON.stringify(track));
    if (!tmpTrack.calculatedCarriers || tmpTrack.calculatedCarriers?.length === 0) {
      tmpTrack.calculatedCarriers = [
        ...getAccessoriesByDescriptionKeyword(accessories, "carrier, Lexium MC12,").map((object) => {
          return { ...object, quantity: 0 };
        })
      ];
      dispatch(setTrack(tmpTrack));
    }
  }, []);

  useUpdateEffect(() => {
    // Empty the calculated guide rails when track changes
    dispatch(setCalculatedGuideRails([]));
    displayOpenTrackErrorMessage && setDisplayOpenTrackErrorMessage(false);
  }, [trackLength]);

  return (
    <>
      <div className="flex flex-col gap-y-4">
        <div className="flex justify-between">
          <div className="flex flex-col gap-y-4">
            <span className="text-lg text-gray-800 font-bold">{t("mc12.ui.step.track.title")}</span>
            <div className="flex flex-row gap-2 ">
              <div className="flex flex-col">
                <div className="flex flex-row gap-4">
                  <div className="w-28 text-sm">{t("mc12.ui.step.track.title.closed")}</div>
                  <div className="flex flex-row gap-x-2 items-center font-bold">
                    {track.isTrackClosed && (
                      <>
                        <span>{t("mc12.ui.step.track.closed.true")}</span>
                        <div className="w-[10px] h-[10px] bg-lifeGreen rounded-full mt-[2px]"> </div>
                      </>
                    )}
                    {!track.isTrackClosed && (
                      <>
                        <span>{t("mc12.ui.step.track.closed.false")}</span>
                        <div className="w-[10px] h-[10px] bg-[#CE0500] rounded-full mt-[2px]"> </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="w-28 text-sm">{t("mc12.ui.step.track.title.length")}</div>
                  <div className="font-bold">
                    {track.length?.toFixed(2)} <span>mm</span>
                  </div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="w-28 text-sm">{t("mc12.ui.step.track.title.overall_x")}</div>
                  <div className="font-bold">
                    {track.overallSizeX?.toFixed(2)} <span>mm</span>
                  </div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="w-28 text-sm">{t("mc12.ui.step.track.title.overall_y")}</div>
                  <div className="font-bold">
                    {track.overallSizeY?.toFixed(2)} <span>mm</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex flex-row gap-4">
                  <div className="w-52 text-sm">{t("mc12.ui.step.track.title.total_segment")}</div>
                  <div className="font-bold">{track.nbSegments}</div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="w-52 text-sm">{t("mc12.ui.step.track.title.total_straight")}</div>
                  <div className="font-bold">{track.nbStraightSegments}</div>
                </div>
                <div className="flex flex-row gap-4">
                  <div className="w-52 text-sm">{t("mc12.ui.step.track.title.total_angled")}</div>
                  <div className="font-bold">{track.nbAngledSegments}</div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <qds-button
              onClick={() => window.open("https://www.se.com/ww/en/download/document/EIO0000004637/", "_blank")}
              icon-name="page-lines"
              size="large"
            ></qds-button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-y-4">
        <span className="text-lg text-gray-800 font-bold">{t("mc12.ui.step.track.title.guiderails")}</span>
        {trackLength === 0 && (
          <span className="text-gray-400 text-sm text-center">{t("mc12.ui.step.track.title.track_empty")}</span>
        )}
        {displayOpenTrackErrorMessage && (
          <span className="text-red-600 text-sm text-center">
            {t("mc12.ui.step.track.error.open_track_start_end_angled_segment")}
          </span>
        )}
        {calculatedGuideRails.length > 0 && (
          <div className="flex flex-col gap-5">
            <div className="flex gap-5">
              <div className="w-1/3"></div>
              <div className="w-1/3">{t("mc12.ui.step.track.title.type")}</div>
              <div className="w-1/3">
                {t("mc12.ui.step.track.title.quantity")} ({t("mc12.ui.step.track.title.sets")})
              </div>
            </div>
            {calculatedGuideRails.map((element) => {
              return (
                <div key={element.commercialReference} className="flex gap-5">
                  <div className="w-1/3">
                    <img className="w-20 h-10" src={getProductImgUrl(element.commercialReference, 50, 50)} alt="" />
                  </div>
                  <div className="w-1/3">
                    {element?.installationDirection?.toLowerCase() === SEGMENT_INF.STRAIGHT.LOGICAL_TYPE
                      ? t("mc12.ui.step.track.title.guiderail_straight", {
                          nb: element.generic
                        })
                      : element?.installationDirection?.toLowerCase() === "stop"
                        ? t("mc12.ui.step.track.title.guiderail_stop")
                        : t(`mc12.ui.step.track.title.guiderail_angled_${element?.installationDirection}`, {
                            nb: 1
                          })}
                  </div>
                  <div className="w-1/3">{element.quantity}</div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-center">
          <qds-button
            {...(!shouldRecalculateGuideRails || trackLength === 0 ? { disabled: "" } : {})}
            importance="emphasized"
            size="large"
            onClick={() => onCalculateGuideRails(track)}
            text={t("mc12.ui.step.track.action.calculate")}
          ></qds-button>
        </div>
      </div>
      <div className="flex gap-x-4">
        <div className="flex flex-col gap-y-4">
          <span className="text-lg text-gray-800 font-bold">{t("mc12.ui.step.track.title.carriers")}</span>
          <QuantityInput
            data-testid="quantity-input-track-info"
            label={t("mc12.ui.step.track.title.carriers.quantity")}
            info={t("mc12.ui.step.track.title.upto_carriers", {
              maxcarriers: getMaxCarriers() || "--"
            })}
            initialValue={getNumberOfCarriers()}
            min={0}
            max={getMaxCarriers()}
            onValueChange={(value) => {
              const tmpTrack = JSON.parse(JSON.stringify(track));
              const numberOfCarriers = value;
              let piece_1_quantity = numberOfCarriers;
              // let piece_10_quantity = 0;
              // let piece_1_quantity = 0;
              // if (numberOfCarriers < 10) {
              //   piece_10_quantity = 0;
              //   piece_1_quantity = numberOfCarriers;
              // } else {
              //   piece_10_quantity = Math.floor(numberOfCarriers / 10);
              //   piece_1_quantity = Math.ceil(numberOfCarriers % 10);
              // }
              const piece_1_index = tmpTrack.calculatedCarriers.findIndex((p) => p.quantityPerSet === "1_PIECE");
              // const piece_10_index = tmpTrack.calculatedCarriers.findIndex((p) => p.quantityPerSet === "10_PIECES");
              tmpTrack.calculatedCarriers[piece_1_index].quantity = piece_1_quantity >= 0 ? piece_1_quantity : 0;
              // tmpTrack.calculatedCarriers[piece_10_index].quantity = piece_10_quantity;
              dispatch(setTrack(tmpTrack));
            }}
            isRequired
          />
        </div>
        <img
          src={getProductImgUrl(track.calculatedCarriers ? track.calculatedCarriers[0]?.commercialReference : null)}
          alt=""
          className="h-28 transform rotate-90"
        />
      </div>
    </>
  );
};

export default TrackInfo;
